import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { EyeIcon, EyeOffIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import { useDragAndDrop } from "react-aria-components";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { type ListData, useListData } from "react-stately";
import type { z } from "zod";

import {
  builtIns,
  create as createSchema,
} from "@blis/shared/schemas/sample-field-set";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectSection,
  SelectTrigger,
} from "@/components/ui/select";
import { TableRAC as Table } from "@/components/ui/table-rac";
import { mutationResponse } from "@/utils/trpc";
import type { SampleFieldSetDetail } from "../../types";
import { TabLayout } from "../TabLayout";

type SampleFieldRow = SampleFieldSetDetail["fields"][number];

type FormValues = z.infer<typeof createSchema>;

export function FieldSetDetailForm({
  fieldSet,
}: {
  fieldSet?: SampleFieldSetDetail;
}) {
  const create = trpc.admin.sampleFieldSet.create.useMutation();
  const update = trpc.admin.sampleFieldSet.update.useMutation();

  const navigate = useNavigate();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      ...fieldSet,
      name: fieldSet?.name || "",
    },
    resolver: zodResolver(createSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (fieldSet) {
      update.mutate(
        { id: fieldSet.id, ...values },
        mutationResponse({
          errorTitle: "Error updating field set",
          successMessage: "Field set updated",
        }),
      );
    } else {
      create.mutate(
        values,
        mutationResponse({
          errorTitle: "Error adding field set",
          successMessage: "Field set added",
          onSuccess(data) {
            navigate({
              to: "/admin/tubes/field-sets/$id/edit",
              params: { id: data.id },
            });
          },
        }),
      );
    }
  };

  return (
    <TabLayout>
      <Card className="mb-4 max-w-3xl">
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  aria-label="Name"
                  id="name"
                  {...field}
                  isInvalid={!!error}
                  validationBehavior="aria"
                  className="w-full"
                >
                  <Label>Name</Label>
                  <Input />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          </form>
        </CardContent>
        <CardFooter className="justify-end">
          <Button
            type="submit"
            isPending={create.isPending || update.isPending}
          >
            Save
          </Button>
        </CardFooter>
      </Card>

      {fieldSet && (
        <Card className="mb-4 max-w-3xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1">
                <CardTitle>Fields</CardTitle>
                <CardDescription>
                  These fields will be shown to users when they submit samples.
                  Users will need to fill out the required fields before they
                  can submit their samples.
                </CardDescription>
              </div>
              {/* <NewFieldSelect fieldSet={fieldSet} /> */}
            </div>
          </CardHeader>
          <CardContent>
            <FieldsTable fieldSet={fieldSet} />
          </CardContent>
        </Card>
      )}
    </TabLayout>
  );
}

function FieldsTable({ fieldSet }: { fieldSet: SampleFieldSetDetail }) {
  const [deleteRow, setDeleteRow] = useState<SampleFieldRow | null>(null);
  const reorder = trpc.admin.sampleFieldSet.updateOrder.useMutation();
  const deleteField = trpc.admin.sampleFieldSet.deleteField.useMutation();

  const list = useListData({
    initialItems: fieldSet?.fields || [],
  });

  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (keys) => {
      return (fieldSet?.fields || [])
        .filter((field) => keys.has(field.id))
        .map((field) => ({
          "text/plain": field.builtIn ?? field.fieldSpec?.name ?? "",
        }));
    },
    onDragEnd(_e) {
      reorder.mutate(
        list.items.map((item, idx) => ({
          id: item.id,
          displayOrder: idx,
        })),
        mutationResponse({
          errorTitle: "Error reordering fields",
          successMessage: "Fields reordered",
          onError(_err) {
            // reset the list to the original state
            for (const field of fieldSet?.fields || []) {
              list.remove(field.id);
              list.append(field);
            }
          },
        }),
      );
    },
    onReorder(e) {
      if (e.target.dropPosition === "before") {
        list.moveBefore(e.target.key, e.keys);
      } else if (e.target.dropPosition === "after") {
        list.moveAfter(e.target.key, e.keys);
      }
    },
  });

  return (
    <>
      <Table
        aria-label="Fields"
        dragAndDropHooks={dragAndDropHooks}
        // selectionMode="multiple"
        // onRowAction={(e) => {
        //   console.log(e);
        // }}
      >
        <Table.Header>
          <Table.Column isRowHeader>Name</Table.Column>
          <Table.Column>Required</Table.Column>
          <Table.Column>Report</Table.Column>
          <Table.Column className="max-w-20" />
        </Table.Header>
        <Table.Body items={list.items}>
          {(field) => (
            <FieldRow
              field={field}
              list={list}
              onDelete={() => setDeleteRow(field)}
            />
          )}
        </Table.Body>
      </Table>
      <div className="p-2 border-t">
        <NewFieldSelect fieldSet={fieldSet} list={list} />
      </div>
      <ConfirmPrompt
        danger
        open={!!deleteRow}
        onOpenChange={() => setDeleteRow(null)}
        onConfirm={() => {
          if (!deleteRow) return;
          deleteField.mutate(
            { id: deleteRow?.id },
            mutationResponse({
              errorTitle: "Error removing field",
              successMessage: "Field removed",
              onSuccess: () => {
                setDeleteRow(null);
                list.remove(deleteRow.id);
              },
            }),
          );
        }}
        message={`Are you sure you want to remove ${deleteRow?.builtIn ?? deleteRow?.fieldSpec?.name}?`}
        loading={deleteField.isPending}
      />
    </>
  );
}

function FieldRow({
  field,
  list,
  onDelete,
  ...props
}: {
  field: SampleFieldRow;
  list: ListData<SampleFieldRow>;
  onDelete: () => void;
} & React.ComponentProps<typeof Table.Row>) {
  const update = trpc.admin.sampleFieldSet.updateField.useMutation();

  const onUpdateRequired = (required: boolean) => {
    list.update(field.id, { ...field, required });

    update.mutate(
      { id: field.id, required, showOnReport: field.showOnReport },
      mutationResponse({
        errorTitle: "Error updating field",
        successMessage: "Field updated",
        onError() {
          const current = list.getItem(field.id) ?? field;
          list.update(field.id, { ...current, required: !required });
        },
      }),
    );
  };

  const onUpdateShowOnReport = (showOnReport: boolean) => {
    list.update(field.id, { ...field, showOnReport });

    update.mutate(
      { id: field.id, required: field.required, showOnReport },
      mutationResponse({
        errorTitle: "Error updating field",
        successMessage: "Field updated",
        onError() {
          const current = list.getItem(field.id) ?? field;
          list.update(field.id, { ...current, showOnReport: !showOnReport });
        },
      }),
    );
  };

  return (
    <Table.Row {...props}>
      <Table.Cell>
        {field.builtIn ? builtIns[field.builtIn] : field.fieldSpec?.name}
      </Table.Cell>
      <Table.Cell>
        {field.required ? (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive"
            onPress={() => onUpdateRequired(false)}
          >
            Required
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onPress={() => onUpdateRequired(true)}
          >
            Optional
          </Button>
        )}
      </Table.Cell>
      <Table.Cell>
        {field.showOnReport ? (
          <Button
            variant="outline"
            size="sm"
            onPress={() => onUpdateShowOnReport(false)}
          >
            <EyeIcon />
            Show
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="text-muted-foreground"
            onPress={() => onUpdateShowOnReport(true)}
          >
            <EyeOffIcon />
            Hide
          </Button>
        )}
      </Table.Cell>
      <Table.Cell className="text-right w-20">
        <div className="flex items-center justify-end gap-2">
          <Button size="icon-sm" variant="ghost" onPress={() => onDelete()}>
            <TrashIcon />
          </Button>
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

function NewFieldSelect({
  fieldSet,
  list,
}: {
  fieldSet: SampleFieldSetDetail;
  list: ListData<SampleFieldRow>;
}) {
  const addField = trpc.admin.sampleFieldSet.addField.useMutation(
    mutationResponse({
      errorTitle: "Error adding field",
      successMessage: "Field added",
      onSuccess(data) {
        list.append(data);
      },
    }),
  );
  const { data: customOptions } = trpc.admin.sampleFieldSpec.list.useQuery();

  const builtInOptions = Object.keys(builtIns)
    .filter(
      (option) => !fieldSet.fields.some((field) => field.builtIn === option),
    )
    .map((option) => ({
      id: option,
      name: builtIns[option],
    }));

  const custom = (customOptions || []).filter(
    (option) =>
      !fieldSet.fields.some((field) => field.fieldSpecId === option.id),
  );

  return (
    <Select
      selectedKey={null}
      aria-label="Add field"
      placeholder="Add a field"
      isDisabled={builtInOptions.length === 0 && custom.length === 0}
      onSelectionChange={(e) => {
        if (typeof e === "string") {
          addField.mutate({
            fieldSetId: fieldSet.id,
            builtIn: e as "COLLECTION_DATETIME",
          });
        } else {
          addField.mutate({
            fieldSetId: fieldSet.id,
            fieldSpecId: e,
          });
        }
      }}
    >
      <SelectTrigger />
      <SelectList>
        {builtInOptions.length > 0 && (
          <SelectSection title="Built-in fields" items={builtInOptions}>
            {(option) => (
              <SelectItem id={option.id} textValue={option.name}>
                {option.name}
              </SelectItem>
            )}
          </SelectSection>
        )}
        {custom.length > 0 && (
          <SelectSection title="Custom fields" items={custom}>
            {(option) => (
              <SelectItem id={option.id} textValue={option.name}>
                {option.name}
              </SelectItem>
            )}
          </SelectSection>
        )}
      </SelectList>
    </Select>
  );
}
